import { Group, GeotabError } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_GROUP } from "@/constants/resultsLimits";

/**
 * Every group in the database (max. {@link RESULTS_LIMIT_GROUP}). `isTruncated` is true when a
 * full page came back, so groups beyond the cap may be missing; a warning is logged once per load.
 */
export function useGroups() {
  const { geotabClient, activeSessionId } = useGeotabClient();
  const query = useQuery<Group[]>({
    queryKey: ["groups", activeSessionId],

    // Ensure activeSessionId is part of the query key
    queryFn: async ({ signal }) => {
      try {
        return warnIfTruncated(
          (await geotabClient.get("Group", {}, RESULTS_LIMIT_GROUP, signal)) ?? [],
        );
      } catch (error) {
        // Some databases reject the typed Group search with "Invalid Id". A plain Get returns the
        // same groups there, and without it every group filter in the add-in stays empty.
        //
        // Only an "Invalid Id" rejection triggers the fallback. The client rejects every call in
        // the same ExecuteMultiCall batch with the same error, so this Get may also see an
        // "Invalid Id" raised by a batch-mate; the fallback is then a single harmless retry.
        // Falling back on any Geotab error would repeat the identical call on an expired session
        // or a permission problem, double the spend under an exhausted quota, and replace the
        // original error with a less informative second one.
        if (!(error instanceof GeotabError) || !/invalid id/i.test(error.message)) {
          throw error;
        }
        return warnIfTruncated(
          (await geotabClient.call<Group[]>(
            "Get",
            { typeName: "Group", resultsLimit: RESULTS_LIMIT_GROUP },
            signal,
          )) ?? [],
        );
      }
    },
  });
  return Object.assign(query, { isTruncated: isFullGroupPage(query.data) });
}

function isFullGroupPage(groups: Group[] | undefined): boolean {
  return (groups?.length ?? 0) >= RESULTS_LIMIT_GROUP;
}

function warnIfTruncated(groups: Group[]): Group[] {
  if (isFullGroupPage(groups)) {
    console.warn(`useGroups: ${groups.length} groups returned, the list may be truncated`);
  }
  return groups;
}
